/**
 * TopProgressBar — barra de progresso fixa no topo da página (à YouTube).
 *
 * Aparece automaticamente para qualquer request HTTP (axios ou fetch)
 * graças aos interceptors globais registados em `App.js`.
 *
 * Estados:
 *   - Indefinida (animação sweep): quando há pedidos em curso mas sem percent
 *   - Determinada (percent 0-100): controlada por `setDeterminate` (uploads,
 *     geração de blobs, etc)
 *
 * Design: laranja Rugged, altura 3px, fixed top, z-index alto para ficar
 * por cima de tudo (nav, modais). Auto-esconde 400ms após terminar.
 */
import React, { useEffect, useState } from 'react';
import { subscribe } from '@/lib/httpProgress';

const HEIGHT = 3;
const BRAND = '#E87A2A';
const BRAND_LIGHT = '#F5A96A';

export default function TopProgressBar() {
  const [s, setS] = useState({ pending: 0, determinate: false, percent: 0, visible: false });
  // `render` fica a `true` até 400ms depois de visible=false, para dar tempo à
  // animação de fade-out. Evita "flash" quando um request começa e acaba
  // muito depressa.
  const [render, setRender] = useState(false);
  // Progresso auto-incremental em modo indeterminado (0 → 90%) para não
  // parecer parado.
  const [autoPct, setAutoPct] = useState(0);

  useEffect(() => subscribe(setS), []);

  useEffect(() => {
    if (s.visible) {
      setRender(true);
      return undefined;
    }
    const t = setTimeout(() => setRender(false), 400);
    return () => clearTimeout(t);
  }, [s.visible]);

  useEffect(() => {
    // Modo indeterminado com fake-progress subindo devagar (não sobe além dos 90%)
    if (!s.visible || s.determinate) {
      setAutoPct(0);
      return undefined;
    }
    setAutoPct(10);
    const id = setInterval(() => {
      setAutoPct((p) => (p >= 90 ? 90 : p + Math.max(1, (90 - p) * 0.08)));
    }, 250);
    return () => clearInterval(id);
  }, [s.visible, s.determinate]);

  if (!render) return null;

  const pct = s.determinate ? s.percent : (s.visible ? autoPct : 100);
  const opacity = s.visible ? 1 : 0;

  return (
    <div
      data-testid="top-progress-bar"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: HEIGHT,
        zIndex: 9999,
        pointerEvents: 'none',
        opacity,
        transition: 'opacity 300ms ease',
      }}
    >
      <div
        data-testid="top-progress-bar-fill"
        style={{
          height: '100%',
          width: `${pct}%`,
          background: `linear-gradient(90deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
          boxShadow: `0 0 8px ${BRAND}, 0 0 4px ${BRAND_LIGHT}`,
          transition: 'width 220ms ease-out',
        }}
      />
    </div>
  );
}
